/**
 * JSON-LD builders for the SEO pages. The Page component accepts a `schema`
 * prop (single object or array) and renders it server-side as
 * <script type="application/ld+json">, so these objects are crawlable.
 */

// Breadcrumb trail, e.g. Hem › Däck › Volvo › XC60. Each item is an absolute
// URL so search engines can render the breadcrumb in results.
export const breadcrumbListSchema = (crumbs, rootUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.label,
    item: `${rootUrl}${crumb.path}`,
  })),
});

// FAQ rich result. `faqs` is [{ question, answer }].
export const faqPageSchema = faqs => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (faqs || []).map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// The live-listings grid as an ItemList where each item is a real Product with
// an Offer (price + availability). This gives the page the Product/Offer
// structured data search engines look for, tied to actual marketplace listings.
// `priceItems` is [{ id, title, priceValue, currency, url }] prepared by the
// page (where Money → number conversion and the canonical URL are available).
// `name` labels the list itself, which the search results page needs.
export const itemListSchema = (priceItems, rootUrl, { brand, image, name } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  ...(name ? { name } : {}),
  itemListElement: (priceItems || []).map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Product',
      name: item.title,
      ...(brand ? { brand: { '@type': 'Brand', name: brand } } : {}),
      ...(image ? { image } : {}),
      url: item.url || `${rootUrl}/l/${item.id}`,
      offers: {
        '@type': 'Offer',
        ...(item.priceValue != null ? { price: item.priceValue } : {}),
        ...(item.currency ? { priceCurrency: item.currency } : {}),
        availability: 'https://schema.org/InStock',
        url: item.url || `${rootUrl}/l/${item.id}`,
      },
    },
  })),
});

// An AggregateOffer summarising the live listings — gives search engines a
// price range signal for the page as a whole (e.g. "däck till Volvo XC60 from
// 792 kr"). Returns null when there are no priced listings.
export const aggregateOfferSchema = (priceItems, name) => {
  const priced = (priceItems || []).filter(i => i.priceValue != null);
  if (priced.length === 0) return null;
  const prices = priced.map(i => i.priceValue);
  const currency = priced[0].currency;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    offers: {
      '@type': 'AggregateOffer',
      offerCount: priced.length,
      lowPrice: Math.min(...prices),
      highPrice: Math.max(...prices),
      ...(currency ? { priceCurrency: currency } : {}),
      availability: 'https://schema.org/InStock',
    },
  };
};
